import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { toast } from "sonner";

const JUR = { US: "U.S.", UK: "U.K.", TH: "Thailand", AU: "Australia", CA: "Canada", DE: "Germany", FR: "France" };

const STATUS_CONFIG = {
  missing:        { label: "Required",          color: "text-rose-700 bg-rose-50 border-rose-200" },
  requested:      { label: "Required",          color: "text-rose-700 bg-rose-50 border-rose-200" },
  needs_revision: { label: "Revision needed",   color: "text-amber-700 bg-amber-50 border-amber-200" },
  awaiting_review:{ label: "Under review",      color: "text-blue-700 bg-blue-50 border-blue-200" },
  approved:       { label: "Accepted",          color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  not_available:  { label: "Unavailable",       color: "text-slate-600 bg-slate-50 border-slate-200" },
};
const CLIENT_ACTIONABLE = new Set(["missing", "requested", "needs_revision"]);

/** Single outstanding-document row with upload + explain actions. */
function OutstandingDocRow({ req, onUploaded, onExplained }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [explaining, setExplaining] = useState(false);
  const cfg = STATUS_CONFIG[req.request_status] || { label: req.request_status, color: "text-slate-600 bg-slate-50 border-slate-200" };
  const actionable = CLIENT_ACTIONABLE.has(req.request_status);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("request_id", req.id);
      const { data } = await portalApi.post("/portal/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.matched_request ? `${file.name} sent for review.` : `${file.name} uploaded.`);
      onUploaded();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "duplicate_document")
        toast.error("This file has already been uploaded.");
      else toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleExplain = async () => {
    if (!explanation.trim()) { toast.error("Please add a brief explanation."); return; }
    setExplaining(true);
    try {
      await portalApi.post(`/portal/documents/requests/${req.id}/explain`, { explanation: explanation.trim() });
      toast.success("Your advisor has been notified.");
      setShowExplain(false); setExplanation("");
      onExplained();
    } catch { toast.error("Could not submit. Please try again."); }
    finally { setExplaining(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3.5 space-y-2.5"
      data-testid={`portal-outstanding-doc-${req.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{req.title}</p>
          {req.linked_document?.filename && (
            <p className="text-[11px] text-slate-400 mt-0.5 truncate">
              <Icons.Paperclip className="h-3 w-3 inline mr-0.5" />
              {req.linked_document.filename}
            </p>
          )}
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium shrink-0 ${cfg.color}`}
          data-testid={`portal-outstanding-doc-status-${req.id}`}>{cfg.label}</span>
      </div>
      {actionable && (
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" className="hidden" onChange={handleFile}
            data-testid={`portal-outstanding-doc-file-input-${req.id}`} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`portal-outstanding-doc-upload-${req.id}`}
            className="text-[12px] bg-indigo-600 text-white rounded-lg px-3 py-1.5 font-medium inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors hover:bg-indigo-700">
            {uploading
              ? <><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Uploading…</>
              : <><Icons.Upload className="h-3.5 w-3.5" />Upload Document</>}
          </button>
          {!showExplain && (
            <button onClick={() => setShowExplain(true)}
              data-testid={`portal-outstanding-doc-explain-${req.id}`}
              className="text-[12px] border border-slate-200 text-slate-600 rounded-lg px-3 py-1.5 font-medium inline-flex items-center gap-1.5 hover:bg-slate-50 transition-colors">
              <Icons.MessageSquare className="h-3.5 w-3.5" />Can&apos;t provide this
            </button>
          )}
        </div>
      )}
      {showExplain && (
        <div className="space-y-2 pt-1 border-t border-slate-100">
          <p className="text-[11px] text-slate-500">Please briefly explain why this document is unavailable:</p>
          <textarea value={explanation} onChange={(e) => setExplanation(e.target.value)} rows={2}
            placeholder="e.g. Not applicable — no foreign bank account"
            data-testid={`portal-outstanding-doc-explain-text-${req.id}`}
            className="w-full text-[12px] rounded-md border border-slate-200 px-2.5 py-1.5 resize-none" />
          <div className="flex items-center gap-2">
            <button onClick={handleExplain} disabled={explaining || !explanation.trim()}
              data-testid={`portal-outstanding-doc-explain-submit-${req.id}`}
              className="text-[12px] bg-slate-700 text-white rounded-lg px-3 py-1.5 font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              {explaining ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Send className="h-3.5 w-3.5" />}Submit
            </button>
            <button onClick={() => { setShowExplain(false); setExplanation(""); }}
              className="text-[12px] border border-slate-200 rounded-lg px-3 py-1.5 text-slate-500 hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** SRE-4 — Client portal Strategic Reviews. Published reports live ONLY here (never emailed).
 * Primary reading surface: native structured report from GET /strategic-reviews/{id}.
 * Graceful fallback: PDF iframe if structured fetch fails.
 * Download PDF and Ask a Question remain available throughout. */
export default function PortalStrategicReviews() {
  const navigate = useNavigate();
  const [reviews, setReviews]   = useState(null);
  const [organizer, setOrganizer] = useState(null); // submitted STR organizer state
  const [outstandingDocs, setOutstandingDocs] = useState(null); // engagement-linked non-STR requests
  const [viewer, setViewer]     = useState(null); // { mode:"native"|"pdf", report?, url?, review }
  const [ask, setAsk]           = useState(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy]         = useState(false);

  const load = useCallback(() => {
    portalApi.get("/portal/strategic-reviews")
      .then((r) => setReviews(r.data.reviews || []))
      .catch(() => setReviews([]));
    // Independent fetch: submitted organizer state (never gated by report publication)
    portalApi.get("/portal/strategic-review")
      .then((r) => setOrganizer(r.data))
      .catch(() => setOrganizer({}));
    // P1: engagement-linked non-STR document requests
    portalApi.get("/portal/outstanding-documents")
      .then((r) => setOutstandingDocs(r.data.requests || []))
      .catch(() => setOutstandingDocs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReport = async (rev) => {
    setBusy(true);
    try {
      // Primary: structured native reader
      const res = await portalApi.get(`/portal/strategic-reviews/${rev.id}`);
      setViewer({ mode: "native", report: res.data, review: rev });
      load(); // refresh list to clear "New" badge (read receipt set by detail endpoint)
    } catch {
      // Graceful fallback to PDF iframe
      try {
        const res = await portalApi.get(`/portal/strategic-reviews/${rev.id}/pdf`, { responseType: "blob" });
        setViewer({ mode: "pdf", url: URL.createObjectURL(res.data), review: rev });
        load();
      } catch { toast.error("Could not open the report"); }
    } finally { setBusy(false); }
  };

  const closeViewer = () => {
    if (viewer?.mode === "pdf" && viewer?.url) URL.revokeObjectURL(viewer.url);
    setViewer(null);
  };

  const downloadPdf = async (rev) => {
    try {
      const res = await portalApi.get(`/portal/strategic-reviews/${rev.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${rev.internal_report_id || "strategic-review"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the report"); }
  };

  // ── RC-1: External STR delivery document actions ──────────────────────────
  // These use the canonical portal document endpoints — the same endpoints the
  // server-side first-access hook (record_first_access) monitors for automatic
  // Review Request and STR cycle closure.
  const viewExtDoc = async (doc, deliveryRev) => {
    setBusy(true);
    try {
      // Inline view: fetch with auth and render in the existing viewer overlay
      // P0-1: correct endpoint is /content; /documents/{id} alone has no handler → 404
      const res = await portalApi.get(`/portal/documents/${doc.id}/content`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setViewer({
        mode: "pdf",
        url,
        review: deliveryRev,
        viewerTitle: doc.label,
        // Download function specific to this document
        downloadFn: async () => {
          const dl = await portalApi.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
          const dlUrl = URL.createObjectURL(dl.data);
          const a = document.createElement("a"); a.href = dlUrl; a.download = doc.label || "document"; a.click();
          URL.revokeObjectURL(dlUrl);
        },
      });
      load(); // refresh to clear "New" badge
    } catch { toast.error("Could not open the document"); }
    finally { setBusy(false); }
  };

  const downloadExtDoc = async (doc) => {
    try {
      const res = await portalApi.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.label || "document"; a.click();
      URL.revokeObjectURL(url);
      load(); // first-access hook fires server-side; refresh list
    } catch { toast.error("Could not download the document"); }
  };

  const submitQuestion = async () => {
    if (!question.trim()) return;
    setBusy(true);
    try {
      await portalApi.post(`/portal/strategic-reviews/${ask.id}/ask`, { question });
      toast.success("Your question was sent to your tax team");
      setAsk(null); setQuestion("");
    } catch { toast.error("Could not send your question"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="portal-strategic-reviews">
      <div className="flex items-center gap-2 mb-1">
        <Icons.FileCheck className="h-5 w-5 text-indigo-600" />
        <h1 className="text-xl font-semibold text-slate-800">Strategic Reviews</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">For your security, your Strategic Tax Reviews are available only here in your Secure Client Portal.</p>

      {/* ── Loading ────────────────────────────────────────────── */}
      {(reviews === null && organizer === null) ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-6"><Icons.Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-6">

          {/* ── Section 1: Submitted STR Organizer (independent of report) ── */}
          {organizer?.review?.status === "submitted" && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5"
              data-testid="portal-str-organizer-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Icons.ClipboardCheck className="h-4 w-4 text-emerald-700 shrink-0" />
                    <span className="text-base font-semibold text-slate-800">Your STR Organizer</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium border border-emerald-200"
                      data-testid="portal-str-organizer-status">Submitted</span>
                  </div>
                  {organizer.review.submitted_at && (
                    <p className="text-[12px] text-slate-500" data-testid="portal-str-organizer-date">
                      Submitted {new Date(organizer.review.submitted_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                      {organizer.review.certification_name && <> · Certified by <span className="font-medium">{organizer.review.certification_name}</span></>}
                    </p>
                  )}
                  <p className="text-[12px] text-slate-500 mt-1">
                    Your Strategic Tax Review Organizer has been certified and received by your tax team.
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate("/portal/strategic-review")}
                  data-testid="portal-str-view-answers-btn"
                  className="text-[12px] bg-emerald-700 text-white rounded-lg px-4 py-2 font-medium inline-flex items-center gap-1.5 hover:bg-emerald-800 transition-colors">
                  <Icons.Eye className="h-3.5 w-3.5" />View Your Submitted Answers
                </button>
              </div>
            </div>
          )}

          {/* ── Section 2: Reports or status placeholder ──────────────── */}
          {(reviews?.length ?? 0) > 0 ? (
            <div className="space-y-4">
              {reviews.map((rev) => (
            rev.type === "external_delivery"
              ? (
                /* ── RC-1: External STR delivery card ─────────────────────────── */
                <div key={rev.id} className="rounded-xl border border-slate-200 bg-white p-5"
                  data-testid={`portal-review-${rev.id}`}>
                  {/* Card header */}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-base font-semibold text-slate-800">{rev.title}</div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap text-[12px] text-slate-500">
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          <Icons.CheckCircle2 className="h-3.5 w-3.5" />Delivered — Ready to View
                        </span>
                        {rev.delivered_at && (
                          <><span>·</span><span>Delivered {new Date(rev.delivered_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span></>
                        )}
                      </div>
                    </div>
                    {!rev.viewed && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium shrink-0">New</span>
                    )}
                  </div>
                  {/* Document rows — each document has View + Download */}
                  <div className="mt-4 space-y-2" data-testid={`portal-ext-delivery-docs-${rev.id}`}>
                    {(rev.documents || []).map((doc) => (
                      <div key={doc.id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5"
                        data-testid={`portal-ext-doc-row-${doc.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Icons.FileText className="h-4 w-4 text-indigo-500 shrink-0" />
                          <span className="text-sm font-medium text-slate-700 truncate">{doc.label}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => viewExtDoc(doc, rev)}
                            disabled={busy}
                            data-testid={`portal-ext-doc-view-${doc.id}`}
                            className="text-[12px] bg-indigo-600 text-white rounded-lg px-3 py-1.5 font-medium inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors">
                            <Icons.Eye className="h-3.5 w-3.5" />View
                          </button>
                          <button
                            onClick={() => downloadExtDoc(doc)}
                            data-testid={`portal-ext-doc-download-${doc.id}`}
                            className="text-[12px] border border-slate-200 text-slate-700 rounded-lg px-3 py-1.5 font-medium inline-flex items-center gap-1.5 hover:bg-slate-50 transition-colors">
                            <Icons.Download className="h-3.5 w-3.5" />Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
              : (
                /* ── Native Allison publication card (existing UI, unchanged) ── */
                <div key={rev.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`portal-review-${rev.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-base font-semibold text-slate-800">{rev.title}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap text-[12px] text-slate-500">
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><Icons.CheckCircle2 className="h-3.5 w-3.5" />Final</span>
                    <span>·</span>
                    <span>Version {rev.version}</span>
                    <span>·</span>
                    <span>Completed {rev.published_at ? new Date(rev.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Prepared by {rev.prepared_by} · {rev.internal_report_id}
                    {(rev.jurisdictions || []).length > 0 && <> · {rev.jurisdictions.map((j) => JUR[j] || j).join(", ")}</>}
                  </div>
                </div>
                {!rev.viewed && <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">New</span>}
              </div>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <button onClick={() => openReport(rev)} disabled={busy} data-testid={`portal-review-view-${rev.id}`}
                  className="text-[12px] bg-indigo-600 text-white rounded-lg px-3 py-1.5 font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                  <Icons.BookOpen className="h-4 w-4" />Read Report
                </button>
                <button onClick={() => downloadPdf(rev)} data-testid={`portal-review-download-${rev.id}`}
                  className="text-[12px] border border-slate-200 text-slate-700 rounded-lg px-3 py-1.5 font-medium inline-flex items-center gap-1.5">
                  <Icons.Download className="h-4 w-4" />Download PDF
                </button>
                <button onClick={() => { setAsk(rev); setQuestion(""); }} data-testid={`portal-review-ask-${rev.id}`}
                  className="text-[12px] border border-slate-200 text-slate-700 rounded-lg px-3 py-1.5 font-medium inline-flex items-center gap-1.5">
                  <Icons.MessageSquare className="h-4 w-4" />Ask a Question
                </button>
              </div>
            </div>
              )
          ))}
        </div>
          ) : organizer?.review?.status === "submitted" ? (
            /* Report in preparation — organizer submitted, report not yet published */
            <div className="rounded-xl border border-slate-200 bg-white p-6"
              data-testid="portal-str-report-pending">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Icons.Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">Strategic Review Report</div>
                  <div className="text-[12px] text-amber-700 font-medium mt-0.5">In preparation</div>
                </div>
              </div>
              <p className="text-[12px] text-slate-500 mt-3">
                Your tax team has received your Organizer and is preparing your Strategic Tax Review report. You will be notified when it is ready.
              </p>
            </div>
          ) : (
            /* No organizer and no reports */
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="portal-reviews-empty">
              <Icons.FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              You don&apos;t have any published Strategic Reviews yet. Your tax team will notify you when one is ready.
            </div>
          )}

          {/* ── Section 3: Outstanding Documents (engagement-scoped, non-STR) ── */}
          {outstandingDocs !== null && outstandingDocs.length > 0 && (
            <div data-testid="portal-outstanding-docs-section">
              <div className="flex items-center gap-2 mb-3">
                <Icons.FolderOpen className="h-4 w-4 text-slate-500" />
                <h2 className="text-sm font-semibold text-slate-700">Outstanding Documents</h2>
                {outstandingDocs.some(r => CLIENT_ACTIONABLE.has(r.request_status)) && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium border border-rose-200"
                    data-testid="portal-outstanding-docs-badge">
                    {outstandingDocs.filter(r => CLIENT_ACTIONABLE.has(r.request_status)).length} required
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mb-3">
                Your tax team requires these documents to complete your engagement.
              </p>
              <div className="space-y-2" data-testid="portal-outstanding-docs-list">
                {outstandingDocs.map((req) => (
                  <OutstandingDocRow
                    key={req.id}
                    req={req}
                    onUploaded={load}
                    onExplained={load}
                  />
                ))}
              </div>
            </div>
          )}

        </div>
      )}

      {/* ── Report Viewer Overlay ─────────────────────────────────── */}
      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/70 flex flex-col p-4" data-testid="portal-review-viewer">
          {/* Header */}
          <div className="flex items-center justify-between text-white mb-3 shrink-0">
            <div className="text-sm font-semibold truncate mr-4" data-testid="portal-review-viewer-title">
              {viewer.review.title}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] text-white/60 hidden sm:block truncate max-w-[180px]">{viewer.viewerTitle || viewer.review.title}</span>
              <button
                onClick={() => viewer.downloadFn ? viewer.downloadFn() : downloadPdf(viewer.review)}
                data-testid="portal-review-viewer-download"
                className="text-[12px] bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors">
                <Icons.Download className="h-4 w-4" />Download
              </button>
              <button onClick={closeViewer} data-testid="portal-review-viewer-close"
                className="text-[12px] bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors">
                <Icons.X className="h-4 w-4" />Close
              </button>
            </div>
          </div>

          {/* Content: native reader or PDF iframe fallback */}
          {viewer.mode === "native" ? (
            <NativeReportReader report={viewer.report} />
          ) : (
            <iframe title={viewer.review.title} src={viewer.url}
              className="flex-1 w-full rounded-lg bg-white" data-testid="portal-review-pdf" />
          )}
        </div>
      )}

      {/* ── Ask a Question Modal ──────────────────────────────────── */}
      {ask && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="portal-review-ask-dialog">
          <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md p-5 space-y-3">
            <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-[12px] text-indigo-800">
              You are asking a question about:<br />
              <b>{ask.title}</b><br />
              <span className="text-[11px] text-indigo-500">Version {ask.version} · Published {ask.published_at ? new Date(ask.published_at).toLocaleDateString() : ""}</span>
            </div>
            <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={4} placeholder="Type your question…"
              data-testid="portal-review-question" className="w-full text-[13px] rounded-md border border-slate-200 px-2.5 py-2 resize-y" />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setAsk(null)} className="text-[12px] border border-slate-200 rounded-lg px-3 py-1.5 text-slate-600">Cancel</button>
              <button onClick={submitQuestion} disabled={busy || !question.trim()} data-testid="portal-review-question-submit"
                className="text-[12px] bg-indigo-600 text-white rounded-lg px-3 py-1.5 font-medium disabled:opacity-40 inline-flex items-center gap-1.5">
                {busy ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Send className="h-3.5 w-3.5" />}Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Native Report Reader ──────────────────────────────────────────────────────
// Renders structured sections from GET /strategic-reviews/{id} in document order.
// PDF download remains available in the overlay header.
function NativeReportReader({ report }) {
  const sections = (report.sections || []).filter(
    (s) => s.body?.trim() || (s.items || []).length || (s.recommendations || []).length
           || (s.risks || []).length || (s.opportunities || []).length
  );
  const summary        = report.summary;
  const evidenceCatalog = report.evidence_catalog || [];
  const [ecOpen, setEcOpen] = useState(false);

  return (
    <div className="flex-1 overflow-y-auto bg-white rounded-lg" data-testid="portal-review-native-reader">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">

        {/* Top-level summary */}
        {summary && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-4" data-testid="portal-review-summary">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 mb-2">Review Summary</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{summary}</p>
          </div>
        )}

        {/* Report sections in document order */}
        {sections.map((sec, i) => (
          <ReportSection key={sec.key || i} sec={sec} />
        ))}

        {/* Evidence catalog — collapsed by default */}
        {evidenceCatalog.length > 0 && (
          <div className="border-t border-slate-100 pt-6" data-testid="portal-review-evidence-catalog">
            <button onClick={() => setEcOpen((o) => !o)}
              className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600 transition-colors">
              <Icons.Library className="h-3.5 w-3.5" />
              Evidence Sources ({evidenceCatalog.length})
              <Icons.ChevronDown className={`h-3.5 w-3.5 transition-transform ${ecOpen ? "rotate-180" : ""}`} />
            </button>
            {ecOpen && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {evidenceCatalog.map((e, i) => (
                  <div key={i} className="text-[11px] text-slate-500 flex gap-1.5">
                    <span className="font-medium text-slate-700 shrink-0">{e.label}:</span>
                    <span className="truncate">{String(e.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Single report chapter ─────────────────────────────────────────────────────
function ReportSection({ sec }) {
  const items = sec.items || [];
  const recs  = sec.recommendations || [];
  const risks = sec.risks || [];
  const opps  = sec.opportunities || [];

  const renderText = (r) =>
    typeof r === "string" ? r : (r.title || r.text || r.recommendation || r.risk || r.opportunity || "");

  return (
    <section data-testid={`portal-review-section-${sec.key}`} className="space-y-3">
      {/* Section heading */}
      <div className="flex items-baseline gap-2 border-b border-slate-100 pb-2">
        {sec.chapter_number != null && (
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide shrink-0">
            {sec.chapter_number}
          </span>
        )}
        <h2 className="text-base font-semibold text-slate-900">{sec.title}</h2>
      </div>

      {/* Body prose */}
      {sec.body?.trim() && (
        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{sec.body.trim()}</p>
      )}

      {/* Structured items (bullet list) */}
      {items.length > 0 && (
        <ul className="space-y-1.5 pl-1">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2 text-sm">
              <span className="text-slate-300 shrink-0 mt-0.5">›</span>
              <div className="text-slate-600">
                {it.title && <span className="font-medium text-slate-800">{it.title}</span>}
                {it.title && it.detail && <span className="text-slate-400"> — </span>}
                {it.detail && <span>{it.detail}</span>}
                {it.recommendation && <span className="block text-[11px] text-emerald-700 mt-0.5">Recommended: {it.recommendation}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Recommendations */}
      {recs.length > 0 && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 space-y-1.5" data-testid={`portal-review-recs-${sec.key}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Recommendations</p>
          {recs.map((r, i) => {
            const txt = renderText(r);
            return txt ? <p key={i} className="text-sm text-slate-700 leading-snug">{txt}</p> : null;
          })}
        </div>
      )}

      {/* Risks */}
      {risks.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 space-y-1.5" data-testid={`portal-review-risks-${sec.key}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Risk Considerations</p>
          {risks.map((r, i) => {
            const txt = renderText(r);
            return txt ? <p key={i} className="text-sm text-slate-700 leading-snug">{txt}</p> : null;
          })}
        </div>
      )}

      {/* Opportunities */}
      {opps.length > 0 && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 space-y-1.5" data-testid={`portal-review-opps-${sec.key}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">Planning Opportunities</p>
          {opps.map((r, i) => {
            const txt = renderText(r);
            return txt ? <p key={i} className="text-sm text-slate-700 leading-snug">{txt}</p> : null;
          })}
        </div>
      )}
    </section>
  );
}
